import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  BuildingOfficeIcon,
  UserIcon,
  LockClosedIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { Card, CardHeader, Button, Input, Avatar, Spinner } from '../components/ui';
import { useAuthStore } from '../stores';
import { authService } from '../services/auth.service';
import { cn } from '../utils/helpers';
import {
  notificationPreferencesService,
  NOTIFICATION_TYPE_LABELS,
} from '../services/notificationPreferences.service';
import type { NotificationPreference } from '../services/notificationPreferences.service';

type SettingsTab = 'business' | 'profile' | 'security' | 'notifications';

interface TabProps {
  id: SettingsTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  activeTab: SettingsTab;
  onClick: (tab: SettingsTab) => void;
}

const Tab: React.FC<TabProps> = ({ id, label, icon: Icon, activeTab, onClick }) => (
  <button
    onClick={() => onClick(id)}
    className={cn(
      'flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors w-full',
      activeTab === id
        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
        : 'text-secondary-600 hover:bg-secondary-50 dark:text-secondary-400 dark:hover:bg-secondary-700/50'
    )}
  >
    <Icon className="h-5 w-5 mr-3" />
    {label}
  </button>
);

export const Settings: React.FC = () => {
  const { user, business } = useAuthStore();
  const [activeTab, setActiveTab] = useState<SettingsTab>('business');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreference[]>([]);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  const [isSavingPreference, setIsSavingPreference] = useState<string | null>(null);

  const businessForm = useForm({
    defaultValues: {
      name: business?.name || '',
      address: business?.address || '',
      city: business?.city || '',
      state: business?.state || '',
      zipCode: business?.zipCode || '',
      phone: business?.phone || '',
    },
  });

  const profileForm = useForm({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
    },
  });

  const securityForm = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  // Fetch notification preferences when tab is activated
  useEffect(() => {
    if (activeTab === 'notifications') {
      fetchNotificationPreferences();
    }
  }, [activeTab]);

  const fetchNotificationPreferences = async () => {
    setIsLoadingPreferences(true);
    try {
      const response = await notificationPreferencesService.getPreferences();
      setNotificationPreferences(response.preferences);
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setIsLoadingPreferences(false);
    }
  };

  const handleTogglePreference = async (type: string) => {
    const currentPref = notificationPreferences.find((p) => p.type === type);
    if (!currentPref) return;

    const newValue = !currentPref.emailEnabled;

    // Optimistic update
    setNotificationPreferences((prev) =>
      prev.map((p) => (p.type === type ? { ...p, emailEnabled: newValue } : p))
    );

    setIsSavingPreference(type);
    try {
      await notificationPreferencesService.updatePreferences([
        { type, emailEnabled: newValue },
      ]);
      toast.success('Preference updated');
    } catch (error) {
      console.error('Failed to update notification preference:', error);
      toast.error('Failed to update preference');
      // Revert on error
      setNotificationPreferences((prev) =>
        prev.map((p) => (p.type === type ? { ...p, emailEnabled: !newValue } : p))
      );
    } finally {
      setIsSavingPreference(null);
    }
  };

  const handleBusinessSubmit = async () => {
    setIsSubmitting(true);
    try {
      // API call would go here
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success('Business settings updated');
    } catch {
      toast.error('Failed to update settings');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfileSubmit = async () => {
    setIsSubmitting(true);
    try {
      // API call would go here
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSecuritySubmit = async (data: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setIsSubmitting(true);
    try {
      await authService.changePassword(data.currentPassword, data.newPassword);
      toast.success('Password updated');
      securityForm.reset();
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Failed to update password';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Settings</h1>
        <p className="text-secondary-500 dark:text-secondary-400">Manage your account and business settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <Card className="lg:col-span-1 h-fit">
          <div className="space-y-1">
            <Tab
              id="business"
              label="Business"
              icon={BuildingOfficeIcon}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <Tab
              id="profile"
              label="Profile"
              icon={UserIcon}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <Tab
              id="security"
              label="Security"
              icon={LockClosedIcon}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
            <Tab
              id="notifications"
              label="Notifications"
              icon={BellIcon}
              activeTab={activeTab}
              onClick={setActiveTab}
            />
          </div>
        </Card>

        {/* Content */}
        <div className="lg:col-span-3">
          {/* Business Settings */}
          {activeTab === 'business' && (
            <Card>
              <CardHeader
                title="Business Information"
                subtitle="Update your company details"
              />
              <form
                onSubmit={businessForm.handleSubmit(handleBusinessSubmit)}
                className="space-y-4"
              >
                <Input
                  label="Business Name"
                  {...businessForm.register('name')}
                />
                <Input
                  label="Address"
                  {...businessForm.register('address')}
                />
                <div className="grid grid-cols-3 gap-4">
                  <Input
                    label="City"
                    {...businessForm.register('city')}
                  />
                  <Input
                    label="State"
                    {...businessForm.register('state')}
                  />
                  <Input
                    label="ZIP Code"
                    {...businessForm.register('zipCode')}
                  />
                </div>
                <Input
                  label="Phone"
                  {...businessForm.register('phone')}
                />
                <div className="flex justify-end pt-4">
                  <Button type="submit" isLoading={isSubmitting}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Profile Settings */}
          {activeTab === 'profile' && (
            <Card>
              <CardHeader
                title="Profile Information"
                subtitle="Update your personal details"
              />
              <form
                onSubmit={profileForm.handleSubmit(handleProfileSubmit)}
                className="space-y-4"
              >
                <div className="flex items-center space-x-4 mb-6">
                  {user && (
                    <Avatar
                      firstName={user.firstName}
                      lastName={user.lastName}
                      src={user.avatar}
                      size="xl"
                    />
                  )}
                  <div>
                    <Button variant="secondary" size="sm">
                      Upload Photo
                    </Button>
                    <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                      JPG or PNG. Max 2MB.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    {...profileForm.register('firstName')}
                  />
                  <Input
                    label="Last Name"
                    {...profileForm.register('lastName')}
                  />
                </div>
                <Input
                  label="Email"
                  type="email"
                  {...profileForm.register('email')}
                />
                <Input
                  label="Phone"
                  {...profileForm.register('phone')}
                />
                <div className="flex justify-end pt-4">
                  <Button type="submit" isLoading={isSubmitting}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <Card>
              <CardHeader
                title="Security Settings"
                subtitle="Update your password"
              />
              <form
                onSubmit={securityForm.handleSubmit(handleSecuritySubmit)}
                className="space-y-4"
              >
                <Input
                  label="Current Password"
                  type="password"
                  {...securityForm.register('currentPassword')}
                />
                <Input
                  label="New Password"
                  type="password"
                  {...securityForm.register('newPassword')}
                />
                <Input
                  label="Confirm New Password"
                  type="password"
                  {...securityForm.register('confirmPassword')}
                />
                <div className="flex justify-end pt-4">
                  <Button type="submit" isLoading={isSubmitting}>
                    Update Password
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {/* Notification Settings */}
          {activeTab === 'notifications' && (
            <Card>
              <CardHeader
                title="Notification Preferences"
                subtitle="Configure how you receive notifications"
              />
              {isLoadingPreferences ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="lg" />
                </div>
              ) : (
                <div className="space-y-4">
                  {notificationPreferences.map((preference) => {
                    const labelInfo = NOTIFICATION_TYPE_LABELS[preference.type];
                    if (!labelInfo) return null;

                    const isToggling = isSavingPreference === preference.type;

                    return (
                      <div
                        key={preference.type}
                        className="flex items-center justify-between p-4 rounded-lg bg-secondary-50 dark:bg-secondary-700/50"
                      >
                        <div>
                          <p className="font-medium text-secondary-900 dark:text-secondary-100">
                            {labelInfo.label}
                          </p>
                          <p className="text-sm text-secondary-500 dark:text-secondary-400">
                            {labelInfo.description}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleTogglePreference(preference.type)}
                          disabled={isToggling}
                          className={cn(
                            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                            preference.emailEnabled
                              ? 'bg-primary-500'
                              : 'bg-secondary-300 dark:bg-secondary-600',
                            isToggling && 'opacity-50 cursor-not-allowed'
                          )}
                          role="switch"
                          aria-checked={preference.emailEnabled}
                        >
                          <span
                            className={cn(
                              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                              preference.emailEnabled ? 'translate-x-5' : 'translate-x-0'
                            )}
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
